import { useState, useMemo } from "react";
import { C } from "@/lib/brand";

// Editorial body-site map for subcutaneous injections. Two views
// (Front / Back), 12 sites total — 8 front + 4 back. Pure inline SVG —
// no images, no external deps — so it renders fast and prints clean.
//
// Props:
//   selected   — site slug currently selected (controlled)
//   onSelect   — (slug) => void
//   heatmap    — { siteSlug: count } usage map for rotation viz
//   readOnly   — bool, disables click handlers
//   testId     — root data-testid
//
// Slugs use {region}-{side}-{position?} to match the BE-anticipated
// vocabulary in /consumer/injection/log.

export const SITE_LABELS = {
  "abdomen-left-upper": "Abdomen · Upper Left",
  "abdomen-right-upper": "Abdomen · Upper Right",
  "abdomen-left-lower": "Abdomen · Lower Left",
  "abdomen-right-lower": "Abdomen · Lower Right",
  "thigh-left": "Outer Thigh · Left",
  "thigh-right": "Outer Thigh · Right",
  "deltoid-left": "Upper Arm · Left",
  "deltoid-right": "Upper Arm · Right",
  "lovehandle-left": "Love Handle · Left",
  "lovehandle-right": "Love Handle · Right",
  "buttock-left": "Upper Outer Glute · Left",
  "buttock-right": "Upper Outer Glute · Right",
};

const FRONT_SITES = [
  // [slug, cx, cy, r] — coordinates inside a 200x420 viewBox.
  { slug: "deltoid-left", cx: 58, cy: 110, r: 14 },
  { slug: "deltoid-right", cx: 142, cy: 110, r: 14 },
  { slug: "abdomen-right-upper", cx: 80, cy: 180, r: 14 },
  { slug: "abdomen-left-upper", cx: 120, cy: 180, r: 14 },
  { slug: "abdomen-right-lower", cx: 80, cy: 220, r: 14 },
  { slug: "abdomen-left-lower", cx: 120, cy: 220, r: 14 },
  { slug: "thigh-right", cx: 78, cy: 305, r: 16 },
  { slug: "thigh-left", cx: 122, cy: 305, r: 16 },
];

const BACK_SITES = [
  { slug: "lovehandle-right", cx: 74, cy: 215, r: 14 },
  { slug: "lovehandle-left", cx: 126, cy: 215, r: 14 },
  { slug: "buttock-right", cx: 78, cy: 270, r: 16 },
  { slug: "buttock-left", cx: 122, cy: 270, r: 16 },
];

export default function SiteMap({
  selected,
  onSelect,
  heatmap = {},
  readOnly = false,
  testId = "site-map",
}) {
  const [view, setView] = useState("front"); // "front" | "back"
  const sites = view === "front" ? FRONT_SITES : BACK_SITES;

  const maxUse = useMemo(
    () => Math.max(1, ...Object.values(heatmap || {})),
    [heatmap]
  );

  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* View toggle */}
      <div
        data-testid={`${testId}-view-toggle`}
        style={{
          display: "inline-flex",
          alignSelf: "center",
          border: `1px solid ${C.ink}`,
          background: C.pure,
        }}
      >
        {[
          { id: "front", label: "FRONT" },
          { id: "back", label: "BACK" },
        ].map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setView(v.id)}
            data-testid={`${testId}-view-${v.id}`}
            style={{
              padding: "10px 22px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.22em",
              border: 0,
              background: view === v.id ? C.ink : "transparent",
              color: view === v.id ? C.cream : C.ink,
              cursor: "pointer",
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div
        style={{
          background: C.pure,
          border: `1px solid ${C.warmGray3}`,
          padding: 18,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <svg
          viewBox="0 0 200 420"
          width="220"
          height="420"
          role="img"
          aria-label={`Injection site map — ${view} view`}
        >
          {/* Body silhouette — minimal editorial line drawing */}
          {view === "front" ? <FrontBody /> : <BackBody />}

          {/* Site dots */}
          {sites.map((s) => {
            const count = heatmap[s.slug] || 0;
            const isSelected = s.slug === selected;
            const heatPct = count / maxUse; // 0..1
            // Recently used / hot — fade toward coral, cool toward peach.
            const fill = isSelected
              ? C.peachDeep
              : count === 0
              ? C.pure
              : interpolate(C.peach, C.coral, heatPct);
            return (
              <g key={s.slug}>
                <circle
                  cx={s.cx}
                  cy={s.cy}
                  r={s.r}
                  fill={fill}
                  stroke={isSelected ? C.ink : C.warmGray}
                  strokeWidth={isSelected ? 2.5 : 1.2}
                  style={{
                    cursor: readOnly ? "default" : "pointer",
                    transition: "all 200ms",
                  }}
                  data-testid={`${testId}-site-${s.slug}`}
                  onClick={() => !readOnly && onSelect && onSelect(s.slug)}
                />
                {count > 0 && (
                  <text
                    x={s.cx}
                    y={s.cy + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="800"
                    fill={heatPct > 0.55 ? C.cream : C.ink}
                    style={{ pointerEvents: "none" }}
                  >
                    {count}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend / selected label */}
      <div style={{ textAlign: "center" }}>
        {selected ? (
          <div data-testid={`${testId}-selected-label`}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
              ★ SELECTED SITE
            </div>
            <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
              {SITE_LABELS[selected] || selected}
            </div>
          </div>
        ) : (
          <div
            data-testid={`${testId}-hint`}
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontSize: 14,
              color: C.warmGray,
            }}
          >
            Tap a circle to pick where you injected.
          </div>
        )}
      </div>

      {Object.keys(heatmap || {}).length > 0 && (
        <div
          data-testid={`${testId}-rotation-legend`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 11,
            color: C.warmGray,
            justifyContent: "center",
          }}
        >
          <span style={{ fontWeight: 800, letterSpacing: "0.16em" }}>ROTATION</span>
          <span
            aria-hidden
            style={{
              width: 80,
              height: 8,
              background: `linear-gradient(90deg, ${C.pure}, ${C.peach}, ${C.coral})`,
              border: `1px solid ${C.warmGray3}`,
            }}
          />
          <span>rested → recently used</span>
        </div>
      )}
    </div>
  );
}

// Minimal SVG silhouettes — front + back views. Designed at 200x420.
function FrontBody() {
  return (
    <g fill="none" stroke={C.warmGray3} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round">
      {/* head */}
      <circle cx="100" cy="42" r="22" />
      {/* neck */}
      <path d="M88 62 L88 78 Q100 84 112 78 L112 62" />
      {/* shoulders + torso */}
      <path d="M52 90 Q100 78 148 90 L156 240 Q138 254 100 256 Q62 254 44 240 Z" />
      {/* arms */}
      <path d="M52 90 Q34 130 38 200 Q42 230 50 250" />
      <path d="M148 90 Q166 130 162 200 Q158 230 150 250" />
      {/* belly button */}
      <circle cx="100" cy="200" r="1.8" fill={C.warmGray} />
      {/* legs */}
      <path d="M62 256 Q56 320 64 390" />
      <path d="M100 256 L100 390" />
      <path d="M138 256 Q144 320 136 390" />
    </g>
  );
}
function BackBody() {
  return (
    <g fill="none" stroke={C.warmGray3} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round">
      {/* head */}
      <circle cx="100" cy="42" r="22" />
      {/* neck */}
      <path d="M88 62 L88 78 Q100 84 112 78 L112 62" />
      {/* shoulders + back */}
      <path d="M52 90 Q100 78 148 90 L156 240 Q138 254 100 256 Q62 254 44 240 Z" />
      {/* spine */}
      <path d="M100 90 L100 250" strokeDasharray="2 3" />
      {/* arms */}
      <path d="M52 90 Q34 130 38 200 Q42 230 50 250" />
      <path d="M148 90 Q166 130 162 200 Q158 230 150 250" />
      {/* glutes */}
      <path d="M62 256 Q56 290 70 305 L100 305 L130 305 Q144 290 138 256" />
      {/* legs */}
      <path d="M70 305 Q62 350 64 390" />
      <path d="M130 305 Q138 350 136 390" />
    </g>
  );
}

// Cheap hex interpolation — used only for the heatmap so a precise
// color-space lerp would be overkill.
function interpolate(a, b, t) {
  const pa = hexToRgb(a);
  const pb = hexToRgb(b);
  const r = Math.round(pa.r + (pb.r - pa.r) * t);
  const g = Math.round(pa.g + (pb.g - pa.g) * t);
  const bl = Math.round(pa.b + (pb.b - pa.b) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}
